import type * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/cn.ts';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectOptionGroup {
  label: string;
  options: readonly SelectOption[];
}

function isGroup(option: SelectOption | SelectOptionGroup): option is SelectOptionGroup {
  return 'options' in option;
}

export interface SelectProps extends Omit<
  React.ComponentProps<'select'>,
  'style' | 'children'
> {
  /**
   * A flat list, or groups of one. The two may be mixed: a handful of loose
   * options above named groups is how "none" and "recent" usually sit.
   */
  options: readonly (SelectOption | SelectOptionGroup)[];
  /**
   * Classes for the box around the select — this is where a width belongs. The
   * chevron is positioned against this element, so constraining the `<select>`
   * alone leaves the arrow behind at the old edge.
   */
  wrapperClassName?: string;
}

/*
 * A native select, styled.
 *
 * Native is deliberate and its scope is narrow: settings rows only. Toolbars
 * use the labelled dropdown-button pattern instead, so a filter never looks
 * like a setting.
 *
 * Groups are `<optgroup>` for the same reason: the platform already draws a
 * grouped list, and every screen reader already says which group the highlighted
 * option is in.
 *
 * `appearance-none` removes the platform chevron, so one is drawn back as a
 * real element rather than a background image: a data-URI background cannot be
 * written as a Tailwind arbitrary value (its spaces break the class name) and
 * would also collide with the fill colour when the classes are merged.
 */
export function Select({ options, className, wrapperClassName, ...props }: SelectProps) {
  return (
    <span
      data-slot='select-wrapper'
      className={cn('relative inline-flex items-center', wrapperClassName)}
    >
      <select
        data-slot='select'
        className={cn(
          'cursor-pointer appearance-none rounded-sm border-none bg-fill py-2.25 pr-7.5 pl-3.5',
          'text-base font-semibold text-ink',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
          className,
        )}
        {...props}
      >
        {options.map((option) =>
          isGroup(option) ? (
            <optgroup key={option.label} label={option.label}>
              {option.options.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </optgroup>
          ) : (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ),
        )}
      </select>
      <ChevronDown
        aria-hidden='true'
        className='pointer-events-none absolute right-3 size-2.5 stroke-3 text-ink'
      />
    </span>
  );
}
